/**
 * Suspension Connector Types (GRE-113)
 *
 * Type definitions for draggable suspension connectors
 * that live between chords on the canvas.
 */

/**
 * Suspension type - the interval pattern
 */
export type SuspensionType = '4-3' | '7-6' | '9-8' | '2-3';

/**
 * Voice part for suspension assignment
 */
export type SuspensionVoice = 'soprano' | 'alto' | 'tenor' | 'bass';

/**
 * Suspension connector object - represents a placed suspension between two chords
 */
export interface SuspensionConnector {
  id: string;

  // Type
  suspensionType: SuspensionType;

  // Connections
  sourceChordId: string;
  targetChordId: string;

  // Voice assignment
  suspendedVoice: SuspensionVoice;
  preparationNote: string;  // Note in source chord (e.g., "G4")
  suspensionNote: string;   // Same note, held over (e.g., "G4")
  resolutionNote: string;   // Resolution note in target chord (e.g., "F#4")

  // Timing
  suspensionDuration: number;  // Beats to hold suspension before resolving

  // State
  selected: boolean;
  playing: boolean;
}

/**
 * Input for creating a new suspension connector
 */
export type SuspensionConnectorInput = Omit<SuspensionConnector, 'id' | 'selected' | 'playing'>;

/**
 * Suspension placement result from voice assignment logic
 */
export interface SuspensionPlacement {
  suspensionType: SuspensionType;
  suspendedVoice: SuspensionVoice;
  preparationNote: string;
  suspensionNote: string;
  resolutionNote: string;
}

/**
 * Validation result for suspension placement
 */
export interface SuspensionValidation {
  valid: boolean;
  reason?: string;
  placement?: SuspensionPlacement;
}

/**
 * Drag data for suspension connector drag-and-drop
 */
export interface SuspensionDragData {
  type: 'suspension-connector';
  suspensionType: SuspensionType;
}

/**
 * Drop zone between two chords
 */
export interface ChordDropZoneData {
  id: string;
  sourceChordId: string;
  targetChordId: string;
  isValid: boolean;
  invalidReason?: string;
}

/**
 * Configuration for each suspension type
 */
export interface SuspensionTypeConfig {
  type: SuspensionType;
  label: string;
  color: string;
  requiredInterval: number;  // Interval from bass in semitones
  resolutionDirection: 'down' | 'up';
  resolutionSteps: number;   // Usually 1 (half or whole step)
}

/**
 * Configuration for all suspension types
 */
export const SUSPENSION_CONFIGS: Record<SuspensionType, SuspensionTypeConfig> = {
  '4-3': {
    type: '4-3',
    label: '4→3',
    color: '#E8A87C',  // Warm amber
    requiredInterval: 5,  // Perfect 4th
    resolutionDirection: 'down',
    resolutionSteps: 1,
  },
  '7-6': {
    type: '7-6',
    label: '7→6',
    color: '#B98B8B',  // Dusty rose
    requiredInterval: 10,  // Minor 7th (or 11 for major 7th)
    resolutionDirection: 'down',
    resolutionSteps: 1,
  },
  '9-8': {
    type: '9-8',
    label: '9→8',
    color: '#D4AF37',  // Gold
    requiredInterval: 14,  // Major 9th (octave + major 2nd)
    resolutionDirection: 'down',
    resolutionSteps: 2,  // Whole step or half step
  },
  '2-3': {
    type: '2-3',
    label: '2→3',
    color: '#8BA888',  // Soft sage
    requiredInterval: 2,  // Major 2nd
    resolutionDirection: 'up',  // Retardation resolves up
    resolutionSteps: 1,
  },
};

/**
 * Get all suspension types as an array
 */
export const SUSPENSION_TYPES: SuspensionType[] = ['4-3', '7-6', '9-8', '2-3'];

/**
 * Default suspension duration in beats
 */
export const DEFAULT_SUSPENSION_DURATION = 2;

// ============================================================================
// Unified Suspension Type (GRE-114)
// ============================================================================

/**
 * Origin of the suspension - user-created or detected from analysis
 */
export type SuspensionOrigin = 'user' | 'detected';

/**
 * Unified Suspension - works for both user-created and detected suspensions
 *
 * This type unifies:
 * - SuspensionConnector (user-created via drag-and-drop)
 * - SuspensionMoment (detected from MusicXML analysis)
 *
 * The goal is for both to look and behave identically in the UI.
 */
export interface UnifiedSuspension {
  id: string;

  // ---- Type ----
  suspensionType: SuspensionType;  // '4-3' | '7-6' | '9-8' | '2-3'

  // ---- Origin ----
  origin: SuspensionOrigin;

  // ---- Connections (for user-created, chord IDs; for detected, may be undefined) ----
  sourceChordId?: string;
  targetChordId?: string;

  // ---- Beat Offsets (for detected suspensions) ----
  preparationOffset?: number;      // Beat where preparation starts
  suspensionOffset?: number;       // Beat where suspension sounds
  resolutionOffset?: number;       // Beat where resolution occurs

  // ---- Voice Assignment ----
  suspendedVoice: SuspensionVoice;
  preparationNote: string;         // Note in source (e.g., "G4")
  suspensionNote: string;          // Same note, held over (e.g., "G4")
  resolutionNote: string;          // Resolution note (e.g., "F#4")

  // ---- Timing ----
  suspensionDuration: number;      // Beats to hold before resolving

  // ---- Bass Context (from backend detection) ----
  bassAtSuspension?: string;       // Bass note when suspension sounds
  intervalFromBass?: number;       // Interval in semitones

  // ---- Chord Context ----
  chordContext?: string;           // Roman numeral or chord label

  // ---- Description ----
  description?: string;            // Human-readable description

  // ---- State (transient) ----
  selected: boolean;
  playing: boolean;
}

/**
 * Convert SuspensionConnector to UnifiedSuspension
 */
export function connectorToUnified(connector: SuspensionConnector): UnifiedSuspension {
  return {
    id: connector.id,
    suspensionType: connector.suspensionType,
    origin: 'user',
    sourceChordId: connector.sourceChordId,
    targetChordId: connector.targetChordId,
    suspendedVoice: connector.suspendedVoice,
    preparationNote: connector.preparationNote,
    suspensionNote: connector.suspensionNote,
    resolutionNote: connector.resolutionNote,
    suspensionDuration: connector.suspensionDuration,
    selected: connector.selected,
    playing: connector.playing,
  };
}

/**
 * Convert UnifiedSuspension back to SuspensionConnector (for backward compatibility)
 * Only works if the suspension has chord IDs (user-created)
 */
export function unifiedToConnector(unified: UnifiedSuspension): SuspensionConnector | null {
  if (!unified.sourceChordId || !unified.targetChordId) {
    return null; // Can't convert detected suspension without chord IDs
  }

  return {
    id: unified.id,
    suspensionType: unified.suspensionType,
    sourceChordId: unified.sourceChordId,
    targetChordId: unified.targetChordId,
    suspendedVoice: unified.suspendedVoice,
    preparationNote: unified.preparationNote,
    suspensionNote: unified.suspensionNote,
    resolutionNote: unified.resolutionNote,
    suspensionDuration: unified.suspensionDuration,
    selected: unified.selected,
    playing: unified.playing,
  };
}

/**
 * Convert an array of SuspensionConnectors to UnifiedSuspensions
 */
export function connectorsToUnified(connectors: SuspensionConnector[]): UnifiedSuspension[] {
  return connectors.map(connectorToUnified);
}

/**
 * Type guard for user-created suspensions
 */
export function isUserCreatedSuspension(
  suspension: UnifiedSuspension
): suspension is UnifiedSuspension & { origin: 'user'; sourceChordId: string; targetChordId: string } {
  return (
    suspension.origin === 'user' &&
    suspension.sourceChordId !== undefined &&
    suspension.targetChordId !== undefined
  );
}

/**
 * Type guard for detected suspensions
 */
export function isDetectedSuspension(
  suspension: UnifiedSuspension
): suspension is UnifiedSuspension & { origin: 'detected' } {
  return suspension.origin === 'detected';
}
